import React, { useEffect, useState } from "react";
import { FaSave, FaTimes, FaUserCircle } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { updateUser, getUserById } from "../../services/userServices";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import { sendNotification } from "./../../services/notificationServices";
import { toast } from "react-toastify";

const INITIAL_DATA = {
  nom: "",
  prenom: "",
  email: "",
  role_id: "",
  photoURL: "",
};

const ROLE_OPTIONS = [
  { value: 1, label: "Administrateur" },
  { value: 2, label: "Bureau, CTF" },
  { value: 3, label: "Membres du Comité" },
  { value: 4, label: "Officiels" },
  { value: 5, label: "Responsables clubs" },
  { value: 6, label: "Utilisateurs" },
  { value: 7, label: "Membres, arbitre, responsable club" },
];

const EditUserModal = ({ isOpen, onClose, refreshList, userId }) => {
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [userData, setUserData] = useState(INITIAL_DATA);
  const [initialRoleId, setInitialRoleId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  // Charger l'utilisateur lorsque la modale s'ouvre
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isOpen || !userId) return;
      setIsLoadingUser(true);
      try {
        const data = await getUserById(userId);
        // On s'assure que role_id est bien numérique
        const roleIdNum = parseInt(data.role_id, 10) || "";
        setUserData({
          nom: data.nom || "",
          prenom: data.prenom || "",
          email: data.email || "",
          role_id: roleIdNum,
          photoURL: data.photoURL || "",
        });
        setInitialRoleId(roleIdNum);
      } catch (error) {
        console.error("Erreur lors du chargement de l'utilisateur:", error);
        toast.error("Erreur lors du chargement des données de l'utilisateur");
      } finally {
        setIsLoadingUser(false);
      }
    };

    if (isOpen) {
      fetchUserData();
      setIsSubmitting(false);
    } else {
      // reset quand on ferme
      setUserData(INITIAL_DATA);
      setInitialRoleId(null);
      setIsLoadingUser(false);
      setIsSubmitting(false);
    }
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === "role_id" ? parseInt(value, 10) || "" : value;
    setUserData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleClose = () => {
    setUserData(INITIAL_DATA);
    setInitialRoleId(null);
    setIsSubmitting(false);
    setIsLoadingUser(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userData.role_id) {
      toast.error("Veuillez sélectionner un rôle.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...userData,
        nom: userData.nom.trim(),
        prenom: userData.prenom.trim(),
        email: userData.email.trim(),
        photoURL: userData.photoURL?.trim() || "",
      };

      await updateUser(userId, payload);

      // Si le rôle a changé → notification à l’utilisateur
      if (
        initialRoleId !== null &&
        payload.role_id &&
        payload.role_id !== initialRoleId
      ) {
        const newRoleName = getRoleFromRoleId(payload.role_id);
        const notificationMessage = `Votre rôle a été mis à jour par un administrateur. Vous avez maintenant le rôle de "${newRoleName}". Veuillez vous déconnecter puis vous reconnecter pour que cela prenne effet.`;

        // Signature existante : (message, userId)
        await sendNotification(notificationMessage, userId);
      }

      toast.success("Utilisateur mis à jour avec succès");
      await refreshList();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'utilisateur:", error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur");
      setIsSubmitting(false);
    }
  };

  const avatarPreview =
    userData.photoURL && userData.photoURL.trim().length > 0
      ? userData.photoURL.trim()
      : "/img/user_avatar.jpg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-2">
      <div className="modal-box max-w-xl w-full bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 p-0 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base sm:text-lg font-bold uppercase tracking-[0.18em] text-white">
              Éditer un utilisateur
            </h3>
            <p className="mt-1 text-xs text-white/80">
              Modifiez les informations, le rôle et l’avatar de ce compte.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-5 py-5 sm:px-6 sm:py-6 space-y-4">
          {isLoadingUser ? (
            <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
              Chargement des données de l’utilisateur…
            </div>
          ) : (
            <>
              {/* Aperçu + infos principales */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Avatar preview */}
                <div className="flex flex-col items-center sm:items-start gap-2 sm:w-1/3">
                  <div className="relative h-20 w-20 rounded-full overflow-hidden ring-2 ring-slate-200 dark:ring-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Aperçu avatar"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FaUserCircle className="h-14 w-14 text-slate-400" />
                    )}
                  </div>
                  <p className="text-[0.7rem] text-slate-500 dark:text-slate-400 text-center sm:text-left">
                    Laissez vide pour conserver ou revenir à l’avatar par défaut.
                  </p>
                </div>

                {/* Nom / prénom / email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:w-2/3">
                  <div>
                    <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
                      Nom
                    </label>
                    <input
                      className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                      type="text"
                      name="nom"
                      value={userData.nom}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
                      Prénom
                    </label>
                    <input
                      className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                      type="text"
                      name="prenom"
                      value={userData.prenom}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
                      Email
                    </label>
                    <input
                      className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                      type="email"
                      name="email"
                      value={userData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Rôle + photoURL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
                    Rôle
                  </label>
                  <select
                    className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                    name="role_id"
                    value={userData.role_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionner un rôle…</option>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[0.7rem] text-slate-500 dark:text-slate-400">
                    En cas de changement, l’utilisateur recevra une notification.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
                    URL photo (optionnel)
                  </label>
                  <input
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                    type="url"
                    name="photoURL"
                    value={userData.photoURL}
                    onChange={handleChange}
                    placeholder="/img/user_avatar.jpg ou URL complète"
                  />
                </div>
              </div>

              {/* Footer actions */}
              <div className="mt-5 flex flex-col sm:flex-row items-center justify-end gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                <Button
                  text="Annuler"
                  icon={FaTimes}
                  type="button"
                  onClick={handleClose}
                  className="btn-ghost gap-2 !px-4 !py-2"
                />
                <Button
                  text={isSubmitting ? "Mise à jour..." : "Mettre à jour"}
                  icon={FaSave}
                  type="submit"
                  className="btn-primary gap-2 !px-4 !py-2"
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
